import { useMemo, useRef, useState } from 'react';

// Çapraz (pivot) tablo — Excel'in "PivotTable Fields" panelinin muadili.
//
// Sağdaki alan listesinden kolonlar SÜRÜKLE-BIRAK ile dört kutuya atılır:
//   Filtreler · Sütunlar · Satırlar · Değerler
// Her kutu ÇOKLU alan alır; satır/sütun alanları iç içe kırılım üretir, değerler kutusundaki
// her alan kendi toplama fonksiyonuyla (Toplam/Ortalama/Adet/En Küçük/En Büyük) ayrı bir
// metrik kolonu olur. Aynı alan hem satırda hem sütunda olamaz; değerlerde ise tekrar edebilir
// (ör. Miktar → Toplam ve Miktar → Ortalama yan yana).
//
// Yapılandırma dışarıdan gelir (useReportView'daki `view.pivot`) → sayfa yenilense de,
// adlı görünüm kaydedilse de pivot düzeni korunur. AG Grid Enterprise'ın pivot'unun
// ücretsiz, kendi motorumuzla yazılmış muadili; dış bağımlılık yok.

const AGGS = [
  { value: 'sum', label: 'Toplam' },
  { value: 'avg', label: 'Ortalama' },
  { value: 'count', label: 'Adet' },
  { value: 'min', label: 'En Küçük' },
  { value: 'max', label: 'En Büyük' },
];
const ZONES = [
  { id: 'filterFields', title: 'Filtreler', icon: '▽' },
  { id: 'colFields', title: 'Sütunlar', icon: '▥' },
  { id: 'rowFields', title: 'Satırlar', icon: '▤' },
  { id: 'valueFields', title: 'Değerler', icon: 'Σ' },
];
const nf = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 });
const BLANK = '—';
// Demet (tuple) anahtarı ayracı: düz birleştirme ['ab','c'] ile ['a','bc'] demetlerini
// aynı anahtara düşürürdü. Veride geçmeyecek bir kontrol karakteri kullanılır.
const SEP = '\u0001';
const tupleKey = (t) => t.join(SEP);

const isNumericCol = (col) => Boolean(col?.num || col?.pct);
const fieldKey = (f) => (typeof f === 'string' ? f : f.key);

function aggregate(vals, fn) {
  if (fn === 'count') return vals.length;
  if (!vals.length) return null;
  const nums = vals.map((v) => Number(v) || 0);
  switch (fn) {
    case 'avg': return nums.reduce((a, b) => a + b, 0) / nums.length;
    case 'min': return Math.min(...nums);
    case 'max': return Math.max(...nums);
    case 'sum':
    default: return nums.reduce((a, b) => a + b, 0);
  }
}

// Sayı gibi duran metinler sayısal, diğerleri Türkçe alfabetik sıralanır.
function compareKeys(a, b) {
  const na = Number(a);
  const nb = Number(b);
  if (a !== '' && b !== '' && !Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
  return String(a).localeCompare(String(b), 'tr');
}

function compareTuples(a, b) {
  for (let i = 0; i < a.length; i += 1) {
    const d = compareKeys(a[i], b[i]);
    if (d !== 0) return d;
  }
  return 0;
}

// İlk açılışta anlamlı bir pivot göster. Kolon sırasına körü körüne uymak işe yaramıyordu:
// ilk metin kolonu genelde KOD'dur (her satır benzersiz → 176 satırlık düz liste), ilk sayısal
// kolon da o raporda tamamen boş olabiliyor (hepsi 0). Bu yüzden seçim VERİYE bakarak yapılır:
//   satır  → tekrar eden, makul sayıda farklı değeri olan ilk metin kolonu (kimlik kolonu değil)
//   değer  → gerçekten dolu ilk sayısal kolon; yoksa "Adet" (kayıt sayımı)
function seedConfig(columns, rows) {
  const sample = rows.slice(0, 300);
  const distinct = (key) => new Set(sample.map((r) => String(r[key] ?? ''))).size;
  const filled = (key) => sample.filter((r) => r[key] !== null && r[key] !== undefined && r[key] !== '' && Number(r[key]) !== 0).length;

  const cats = columns.filter((c) => !isNumericCol(c));
  // Kimlik gibi davranan kolonu (her satır farklı) satır alanı yapma; 1-40 arası kırılım ideal.
  const goodCat = sample.length
    ? cats.find((c) => { const d = distinct(c.key); return d > 1 && d <= 40 && d < sample.length * 0.6; })
    : null;
  const rowCol = goodCat || cats[0] || columns[0];

  // agg:'none' işaretli kolonlar (birim fiyat, KDV, ağırlık…) toplanmaya uygun değil —
  // rapor onları zaten "toplama" dışında tutuyor, varsayılan metrik de olmamalılar.
  const nums = columns.filter((c) => isNumericCol(c) && c.agg !== 'none');
  const goodNum = sample.length ? nums.find((c) => filled(c.key) > 0) : nums[0];

  return {
    filterFields: [],
    colFields: [],
    rowFields: rowCol ? [rowCol.key] : [],
    // Dolu sayısal kolon yoksa metrik olarak kayıt sayımı: her raporda anlamlı bir çıktı verir.
    valueFields: goodNum
      ? [{ key: goodNum.key, agg: 'sum' }]
      : (rowCol ? [{ key: rowCol.key, agg: 'count' }] : []),
    filterValues: {},
  };
}

// Kolon seti değişmiş olabilir (rapor güncellendi, saklı görünüm eski): geçersiz alanları at.
function sanitize(cfg, columns) {
  const valid = new Set(columns.map((c) => c.key));
  const keep = (arr) => (arr || []).filter((f) => valid.has(fieldKey(f)));
  return {
    filterFields: keep(cfg.filterFields),
    colFields: keep(cfg.colFields),
    rowFields: keep(cfg.rowFields),
    valueFields: keep(cfg.valueFields).map((v) => (typeof v === 'string' ? { key: v, agg: 'sum' } : v)),
    filterValues: cfg.filterValues || {},
    panelWidth: clampPanel(cfg.panelWidth),
  };
}

// Veri henüz gelmediyse (rows boş) kullanılan boş düzen — tohum veriyle üretilir.
const EMPTY_CFG = { filterFields: [], colFields: [], rowFields: [], valueFields: [], filterValues: {} };

const PANEL_MIN = 240;
const PANEL_MAX = 680;
const PANEL_DEFAULT = 320;
const clampPanel = (w) => Math.min(PANEL_MAX, Math.max(PANEL_MIN, Number(w) || PANEL_DEFAULT));

export function ReportPivot({ rows, columns, config, onConfigChange }) {
  // Tohum bir kez, VERİ GELDİKTEN sonra üretilir (satır yokken kolon adına bakmak zorunda
  // kalırdık; dolu/boş ayrımı ancak veriyle yapılabiliyor).
  const seeded = useRef(null);
  if (!seeded.current && rows.length) seeded.current = seedConfig(columns, rows);
  const cfg = useMemo(
    () => sanitize(config || seeded.current || EMPTY_CFG, columns),
    [config, columns],
  );

  const [dragZone, setDragZone] = useState(null);   // üzerine sürüklenen kutu (vurgu için)
  const [openFilter, setOpenFilter] = useState(null); // açık filtre açılır listesi

  const colByKey = useMemo(() => new Map(columns.map((c) => [c.key, c])), [columns]);
  const labelOf = (key) => colByKey.get(key)?.label || key;
  // Satır/sütun başlıklarında ham değer değil kolonun kendi biçimi görünsün
  // (ör. Başlangıç: '1999-12-31' yerine '31.12.1999'). Gruplama anahtarı ham değer kalır.
  const displayKey = (fieldName, raw) => {
    const col = colByKey.get(fieldName);
    if (!col?.fmt || raw === BLANK) return raw;
    try { return col.fmt(raw); } catch { return raw; }
  };

  const apply = (next) => onConfigChange?.(next);

  // ── Sürükle-bırak ──
  const startDrag = (e, key, from, index) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({ key, from, index }));
  };

  const dropInto = (zone, e, dropIndex = null) => {
    e.preventDefault();
    setDragZone(null);
    let payload;
    try { payload = JSON.parse(e.dataTransfer.getData('text/plain')); } catch { return; }
    if (!payload?.key || !colByKey.has(payload.key)) return;

    const next = {
      filterFields: [...cfg.filterFields],
      colFields: [...cfg.colFields],
      rowFields: [...cfg.rowFields],
      valueFields: cfg.valueFields.map((v) => ({ ...v })),
      filterValues: { ...cfg.filterValues },
    };

    // Kaynaktan çıkar (liste dışından geliyorsa)
    if (payload.from && payload.from !== 'list') next[payload.from].splice(payload.index, 1);

    // Satır/Sütun/Filtre birbirini dışlar — aynı alan ikisinde birden kırılım üretemez.
    if (zone !== 'valueFields') {
      ['filterFields', 'colFields', 'rowFields'].forEach((z) => {
        next[z] = next[z].filter((f) => fieldKey(f) !== payload.key);
      });
    } else if (payload.from === 'list' && next.valueFields.some((v) => v.key === payload.key)) {
      // Listeden aynı alanı ikinci kez atmak farklı bir toplama içindir; engellemiyoruz.
    }

    const col = colByKey.get(payload.key);
    const item = zone === 'valueFields'
      ? { key: payload.key, agg: isNumericCol(col) ? 'sum' : 'count' }
      : payload.key;
    const at = dropIndex === null || dropIndex > next[zone].length ? next[zone].length : dropIndex;
    next[zone].splice(at, 0, item);
    apply(next);
  };

  const removeFrom = (zone, index) => {
    const next = { ...cfg, [zone]: cfg[zone].filter((_, i) => i !== index) };
    apply(next);
  };

  const setAgg = (index, agg) => {
    apply({ ...cfg, valueFields: cfg.valueFields.map((v, i) => (i === index ? { ...v, agg } : v)) });
  };

  // Alan listesinden tıklama: sürüklemek istemeyen için kısayol — metin satıra, sayı değerlere.
  const quickAdd = (key) => {
    const col = colByKey.get(key);
    const zone = isNumericCol(col) ? 'valueFields' : 'rowFields';
    if (zone === 'rowFields' && cfg.rowFields.includes(key)) return;
    const item = zone === 'valueFields' ? { key, agg: isNumericCol(col) ? 'sum' : 'count' } : key;
    apply({ ...cfg, [zone]: [...cfg[zone], item] });
  };

  const clearAll = () => apply({
    filterFields: [], colFields: [], rowFields: [], valueFields: [], filterValues: {},
    panelWidth: cfg.panelWidth,
  });

  // ── Panel genişliği: aradaki tutamaç ile sürüklenir, düzenle birlikte kalıcılaşır ──
  const [liveWidth, setLiveWidth] = useState(null);
  const panelWidth = liveWidth ?? cfg.panelWidth;
  const startResize = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = cfg.panelWidth;
    let last = startW;
    const onMove = (ev) => {
      last = clampPanel(startW + (startX - ev.clientX)); // sola sürükle = panel genişler
      setLiveWidth(last);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      setLiveWidth(null);
      apply({ ...cfg, panelWidth: last });
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // ── Filtre kutusundaki alanların seçilebilir değerleri ──
  const filterOptions = useMemo(() => {
    const out = {};
    cfg.filterFields.forEach((k) => {
      out[k] = [...new Set(rows.map((r) => String(r[k] ?? BLANK)))].sort(compareKeys);
    });
    return out;
  }, [rows, cfg.filterFields]);

  const toggleFilterValue = (key, value) => {
    const cur = cfg.filterValues[key] || [];
    const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
    apply({ ...cfg, filterValues: { ...cfg.filterValues, [key]: next } });
  };

  // ── Hesap ──
  const model = useMemo(() => {
    const { rowFields, colFields, valueFields, filterFields, filterValues } = cfg;

    const data = rows.filter((r) => filterFields.every((k) => {
      const sel = filterValues[k];
      if (!sel || sel.length === 0) return true;         // seçim yok = tümü
      return sel.includes(String(r[k] ?? BLANK));
    }));

    const tupleOf = (r, fields) => fields.map((f) => String(r[f] ?? BLANK));
    const uniq = (fields) => {
      if (!fields.length) return [];
      const map = new Map();
      data.forEach((r) => {
        const t = tupleOf(r, fields);
        map.set(tupleKey(t), t);
      });
      return [...map.values()].sort(compareTuples);
    };

    const rowTuples = uniq(rowFields);
    const colTuples = uniq(colFields);

    // cells[rowKey][colKey][valueIndex] = ham değer listesi
    const cells = new Map();
    const push = (rk, ck, vi, v) => {
      let byCol = cells.get(rk);
      if (!byCol) { byCol = new Map(); cells.set(rk, byCol); }
      let byVal = byCol.get(ck);
      if (!byVal) { byVal = valueFields.map(() => []); byCol.set(ck, byVal); }
      byVal[vi].push(v);
    };
    data.forEach((r) => {
      const rk = rowFields.length ? tupleKey(tupleOf(r, rowFields)) : '';
      const ck = colFields.length ? tupleKey(tupleOf(r, colFields)) : '';
      valueFields.forEach((v, vi) => push(rk, ck, vi, r[v.key]));
    });

    const cellAgg = (rk, ck, vi) => {
      const vals = cells.get(rk)?.get(ck)?.[vi];
      return vals && vals.length ? aggregate(vals, valueFields[vi].agg) : null;
    };
    const rowAgg = (rk, vi) => {
      const byCol = cells.get(rk);
      if (!byCol) return null;
      const all = [];
      byCol.forEach((byVal) => { all.push(...byVal[vi]); });
      return all.length ? aggregate(all, valueFields[vi].agg) : null;
    };
    const colAgg = (ck, vi) => {
      const all = [];
      cells.forEach((byCol) => { const b = byCol.get(ck); if (b) all.push(...b[vi]); });
      return all.length ? aggregate(all, valueFields[vi].agg) : null;
    };
    const grandAgg = (vi) => {
      const all = [];
      cells.forEach((byCol) => { byCol.forEach((byVal) => { all.push(...byVal[vi]); }); });
      return all.length ? aggregate(all, valueFields[vi].agg) : null;
    };

    return { rowTuples, colTuples, cellAgg, rowAgg, colAgg, grandAgg, dataCount: data.length };
  }, [rows, cfg]);

  const { rowFields, colFields, valueFields } = cfg;
  const hasLayout = valueFields.length > 0 && (rowFields.length > 0 || colFields.length > 0);

  const fmtValue = (vi, v) => {
    if (v === null || v === undefined) return '';
    const vf = valueFields[vi];
    const col = colByKey.get(vf.key);
    return (col?.fmt && vf.agg !== 'count') ? col.fmt(v) : nf.format(v);
  };
  const valueHeader = (vi) => {
    const vf = valueFields[vi];
    const agg = AGGS.find((a) => a.value === vf.agg)?.label || vf.agg;
    return `${labelOf(vf.key)} · ${agg}`;
  };

  // Sütun alanı varsa yaprak kolonlar = (sütun demeti × değer); yoksa sadece değerler.
  const leafCols = useMemo(() => {
    if (!colFields.length) return valueFields.map((_, vi) => ({ ck: '', vi }));
    return model.colTuples.flatMap((t) => valueFields.map((_, vi) => ({ ck: tupleKey(t), tuple: t, vi })));
  }, [model.colTuples, colFields.length, valueFields]);

  // Sütun alanı varsa başlık şu düzende kurulur (Excel'in "Sütun Etiketleri" mantığı):
  //   satır 0..C-1 : solda O SEVİYENİN ALAN ADI (ör. "Distribütör"), sağda o alanın değerleri
  //   son satır    : solda SATIR alanlarının adları, altta her yaprak kolonun metrik etiketi
  // Böylece hangi kırılımın hangi alandan geldiği başlıkta okunur.
  const showTotals = colFields.length > 0;

  return (
    <div className="rg-pivot">
      <div className="rg-pivot-body">
        {/* ── Sol: pivot tablo ── */}
        <div className="rg-scroll rg-pivot-scroll">
          {!hasLayout ? (
            <div className="rg-pivot-placeholder">
              <div className="rg-pivot-placeholder-icon">▦</div>
              <div className="rg-pivot-placeholder-title">Pivot tablo oluşturmak için alan seçin</div>
              <div className="rg-pivot-placeholder-text">
                Sağdaki listeden bir kolonu <b>Satırlar</b>, <b>Sütunlar</b>, <b>Değerler</b> ya da
                <b> Filtreler</b> kutusuna sürükleyin. En az bir <b>Değer</b> ve bir <b>Satır</b> veya
                <b> Sütun</b> alanı gerekir.
              </div>
            </div>
          ) : (
            <table className="rg-table rg-pivot-table">
              <thead>
                {colFields.length > 0 ? (
                  <>
                    {colFields.map((cf, level) => (
                      <tr key={cf}>
                        <th
                          className="rg-th rg-th-sticky rg-pivot-colfield"
                          colSpan={Math.max(rowFields.length, 1)}
                        >
                          {labelOf(cf)} ▸
                        </th>
                        {groupHeaderCells(model.colTuples, level, valueFields.length).map((g) => (
                          <th key={`${level}-${g.start}`} className="rg-th rg-pivot-colgrp" colSpan={g.span}>
                            {displayKey(cf, g.label)}
                          </th>
                        ))}
                        {level === 0 && (
                          <th
                            className="rg-th rg-pivot-total-h"
                            rowSpan={colFields.length}
                            colSpan={valueFields.length}
                          >
                            TOPLAM
                          </th>
                        )}
                      </tr>
                    ))}
                    <tr>
                      {(rowFields.length ? rowFields : ['']).map((rf, i) => (
                        <th key={rf || i} className={`rg-th${i === 0 ? ' rg-th-sticky' : ''} rg-pivot-corner`}>
                          {rf ? labelOf(rf) : ''}
                        </th>
                      ))}
                      {leafCols.map((lc, i) => (
                        <th key={`v-${i}`} className="rg-th rg-pivot-valh">{valueHeader(lc.vi)}</th>
                      ))}
                      {valueFields.map((_, vi) => (
                        <th key={`tv-${vi}`} className="rg-th rg-pivot-total-h">{valueHeader(vi)}</th>
                      ))}
                    </tr>
                  </>
                ) : (
                  <tr>
                    {rowFields.map((rf) => (
                      <th key={rf} className="rg-th rg-th-sticky rg-pivot-corner">{labelOf(rf)}</th>
                    ))}
                    {valueFields.map((_, vi) => (
                      <th key={`v-${vi}`} className="rg-th rg-pivot-valh">{valueHeader(vi)}</th>
                    ))}
                  </tr>
                )}
              </thead>

              <tbody>
                {model.rowTuples.map((t) => {
                  const rk = tupleKey(t);
                  return (
                    <tr key={rk}>
                      {t.map((cell, i) => (
                        <td key={i} className={`rg-td${i === 0 ? ' rg-td-sticky' : ''} rg-pivot-rowh`}>
                          {displayKey(rowFields[i], cell)}
                        </td>
                      ))}
                      {leafCols.map((lc, i) => (
                        <td key={i} className="rg-td rg-pivot-num">{fmtValue(lc.vi, model.cellAgg(rk, lc.ck, lc.vi))}</td>
                      ))}
                      {showTotals && valueFields.map((_, vi) => (
                        <td key={`t-${vi}`} className="rg-td rg-pivot-total rg-pivot-num">{fmtValue(vi, model.rowAgg(rk, vi))}</td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>

              <tfoot>
                <tr className="rg-totals-row">
                  <td className="rg-td rg-td-sticky" colSpan={Math.max(rowFields.length, 1)}>
                    <span className="rg-totals-label">GENEL TOPLAM</span>
                  </td>
                  {leafCols.map((lc, i) => (
                    <td key={i} className="rg-td rg-pivot-num">
                      {fmtValue(lc.vi, colFields.length ? model.colAgg(lc.ck, lc.vi) : model.grandAgg(lc.vi))}
                    </td>
                  ))}
                  {showTotals && valueFields.map((_, vi) => (
                    <td key={`g-${vi}`} className="rg-td rg-pivot-total rg-pivot-num">{fmtValue(vi, model.grandAgg(vi))}</td>
                  ))}
                </tr>
              </tfoot>
            </table>
          )}
        </div>

        {/* Panel genişliği tutamacı */}
        <div
          className="rg-pivot-resizer"
          onMouseDown={startResize}
          role="separator"
          aria-orientation="vertical"
          title="Paneli genişlet / daralt"
        />

        {/* ── Sağ: alan listesi + kutular ── */}
        <aside className="rg-pivot-panel" style={{ width: panelWidth, flexBasis: panelWidth }}>
          <div className="rg-panel-head">
            <span className="rg-panel-title">Pivot Alanları</span>
            <button type="button" className="rg-panel-link" onClick={clearAll}>Temizle</button>
          </div>

          <div className="rg-pivot-fields">
            <div className="rg-pivot-fields-hint">Alanı aşağıdaki kutulara sürükleyin (tıklamak da ekler)</div>
            {columns.map((c) => (
              <div
                key={c.key}
                className="rg-pivot-field"
                draggable
                onDragStart={(e) => startDrag(e, c.key, 'list')}
                onClick={() => quickAdd(c.key)}
                title={isNumericCol(c) ? 'Sayısal alan — Değerler kutusuna uygun' : 'Metin alanı — Satır/Sütun/Filtre kutusuna uygun'}
              >
                <span className="rg-panel-grip">⠿</span>
                <span className="rg-pivot-field-label">{c.label}</span>
                {isNumericCol(c) && <span className="rg-panel-tag">Σ</span>}
              </div>
            ))}
          </div>

          <div className="rg-pivot-zones">
            {ZONES.map((z) => (
              <div
                key={z.id}
                className={`rg-pivot-zone${dragZone === z.id ? ' dragover' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setDragZone(z.id); }}
                onDragLeave={() => setDragZone((cur) => (cur === z.id ? null : cur))}
                onDrop={(e) => dropInto(z.id, e)}
              >
                <div className="rg-pivot-zone-head"><span className="rg-pivot-zone-ico">{z.icon}</span>{z.title}</div>
                <div className="rg-pivot-zone-body">
                  {cfg[z.id].length === 0 && <div className="rg-pivot-zone-empty">buraya sürükle</div>}

                  {z.id === 'valueFields' && cfg.valueFields.map((v, i) => (
                    <div
                      key={`${v.key}-${i}`}
                      className="rg-pivot-chip"
                      draggable
                      onDragStart={(e) => startDrag(e, v.key, 'valueFields', i)}
                    >
                      <span className="rg-pivot-chip-label">{labelOf(v.key)}</span>
                      <select
                        className="rg-pivot-chip-agg"
                        value={v.agg}
                        onChange={(e) => setAgg(i, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {AGGS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                      </select>
                      <button type="button" className="rg-pivot-chip-x" onClick={() => removeFrom('valueFields', i)}>×</button>
                    </div>
                  ))}

                  {z.id !== 'valueFields' && cfg[z.id].map((key, i) => (
                    <div
                      key={`${key}-${i}`}
                      className="rg-pivot-chip"
                      draggable
                      onDragStart={(e) => startDrag(e, key, z.id, i)}
                    >
                      <span className="rg-pivot-chip-label">{labelOf(key)}</span>
                      {z.id === 'filterFields' && (
                        <button
                          type="button"
                          className="rg-pivot-chip-filter"
                          onClick={() => setOpenFilter(openFilter === key ? null : key)}
                        >
                          {(cfg.filterValues[key]?.length) ? `${cfg.filterValues[key].length} seçili` : 'tümü'}
                        </button>
                      )}
                      <button type="button" className="rg-pivot-chip-x" onClick={() => removeFrom(z.id, i)}>×</button>

                      {z.id === 'filterFields' && openFilter === key && (
                        // draggable=false: açılır liste sürüklenebilir çipin İÇİNDE; olmazsa
                        // kutucuğa basmak çipi sürüklemeye başlatır.
                        <div
                          className="rg-pivot-filter-pop"
                          draggable={false}
                          onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            className="rg-panel-link"
                            onClick={() => apply({ ...cfg, filterValues: { ...cfg.filterValues, [key]: [] } })}
                          >
                            Tümünü seç
                          </button>
                          <div className="rg-pivot-filter-list">
                            {(filterOptions[key] || []).map((opt) => (
                              <label key={opt} className="rg-pivot-filter-opt">
                                <input
                                  type="checkbox"
                                  checked={(cfg.filterValues[key] || []).includes(opt)}
                                  onChange={() => toggleFilterValue(key, opt)}
                                />
                                <span>{displayKey(key, opt)}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="rg-panel-hint">
            {model.dataCount} satır özetleniyor
            {cfg.filterFields.length > 0 ? ' (filtre uygulandı)' : ''}
          </div>
        </aside>
      </div>
    </div>
  );
}

/**
 * Bir sütun seviyesinin başlık hücrelerini üretir: aynı üst kırılıma düşen ardışık
 * demetleri tek hücrede birleştirir (colSpan). span = kaç yaprak kolon kapsadığı.
 */
function groupHeaderCells(colTuples, level, valueCount) {
  const cells = [];
  let start = 0;
  while (start < colTuples.length) {
    const prefix = tupleKey(colTuples[start].slice(0, level + 1));
    let end = start + 1;
    while (end < colTuples.length && tupleKey(colTuples[end].slice(0, level + 1)) === prefix) end += 1;
    cells.push({ start, span: (end - start) * valueCount, label: colTuples[start][level] });
    start = end;
  }
  return cells;
}
